import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { getIsAuthenticated } from '@/redux/selectors/auth/user';
import { saveRecentSearch, useRecentSearches } from '@/services/getRecentSearches';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { guardedJsonParse } from '@/utility/json';
import { toCamelCase } from '@/utility/strings';

// we limit recent searches based on usage. By default we limit to the most
// recent MAX_RECENT_SEARCHES but when using auto complete user may see older
// searches exposed
export const MAX_RECENT_SEARCHES = 6;
const MAX_STORED_SEARCHES = 50;

type TRecentSearchOption = {
  label: string;
  sublabel?: string;
  value: string;
  url: string;
  timestamp: number;
  groupName: string;
};

// some filters need to be converted to snake case when used in search
// but our UI works with camel case (as defined in the ESearchFilters enum)
// retaining them in snake case may alter the search results (param could be deleted)
// or the relevant filter pill may not reflect that the filter is being used
const SNAKE_CASE_FILTERS = new Set([
  'trailer_weight[gte]',
  'trailer_weight[lte]',
  'unlimited_generator',
  'unlimited_miles',
  'delivery_stationary',
  'instant_book',
]);

function isRecentSearchFilter(obj: unknown): obj is TRecentSearchOption {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const { label, url, value } = obj as TRecentSearchOption;
  return typeof label === 'string' && typeof url === 'string' && typeof value === 'string';
}

function parsedObjectIsRecentSearchArray(obj: unknown): obj is TRecentSearchOption[] {
  return Array.isArray(obj) && obj.every(item => isRecentSearchFilter(item));
}

function formatSearchUrl(searchUrl: string): string {
  return `/rv-search?${searchUrl.split('?')[1]}`;
}

function formatRecentSearchLabel(
  currentLabel: string,
  currentSublabel?: string,
): { label: string; sublabel: string } {
  const defaultLabel = currentLabel;
  const defaultSublabel = currentSublabel ?? '';

  const labelSplit = currentLabel.split(' • ');
  const builtLabel = labelSplit[0]?.trim();
  const builtSublabel = labelSplit.slice(1).join(' • ').trim();
  const useBuiltLabel = builtLabel && builtSublabel;
  return {
    label: useBuiltLabel ? builtLabel : defaultLabel,
    sublabel: useBuiltLabel ? builtSublabel : defaultSublabel,
  };
}

function filterRecentSearchByRentalType(
  type: ERentalCategory,
  searchOptions: TRecentSearchOption[],
) {
  // Filter recent search options based on rental type
  return searchOptions.filter(searchOption => {
    const queryParams = searchOption.url.split('?')?.[1];
    const searchParams = new URLSearchParams(queryParams);

    // If the rental type is RV and the rental category is not present,
    // or if the rental type matches the rental category, return true
    if (type === ERentalCategory.RV) {
      return (
        !searchParams.has(ESearchFilters.RENTAL_CATEGORY) ||
        searchParams.get(ESearchFilters.RENTAL_CATEGORY) === type
      );
    } else {
      // For other rental types, return true if the rental category matches the type
      return searchParams.get(ESearchFilters.RENTAL_CATEGORY) === type;
    }
  });
}

export function useRecentSearchesWithFilters(
  searchRentalType = ERentalCategory.RV,
): [TRecentSearchOption[], { addRecentSearch: (search: TRecentSearchOption) => void }] {
  const [recentSearches, { update }] = useLocalStorage('recentSearchesWithFilters');
  const [sessionSearches, { update: updateSessionSearches }] = useLocalStorage(
    'sessionRecentSearchesWithFilters',
  );
  const isAuthenticated = useSelector(getIsAuthenticated);
  const { data: fetchedSearches, mutate: updateFetchedSearches } = useRecentSearches({
    isAuthenticated,
  });

  useEffect(() => {
    if (isAuthenticated && fetchedSearches) {
      const parsedFetchedSearches = fetchedSearches.map(search => {
        const { label, sublabel } = formatRecentSearchLabel(search.label);

        return {
          label,
          sublabel,
          value: search.value,
          url: formatSearchUrl(search.search_url),
          timestamp: search.timestamp,
          groupName: 'recentSearches',
        };
      });
      const localSearches =
        guardedJsonParse(parsedObjectIsRecentSearchArray, recentSearches ?? '') || [];
      const parsedLocalSearches = localSearches.map(search => {
        const { label, sublabel } = formatRecentSearchLabel(search.label, search.sublabel);

        return {
          label,
          sublabel,
          value: search.value,
          url: formatSearchUrl(search.url),
          timestamp: search.timestamp,
          groupName: 'recentSearches',
        };
      });
      const combinedSearches = [...parsedFetchedSearches, ...parsedLocalSearches];

      const uniqueSearches = combinedSearches.filter(
        (search, index, self) => index === self.findIndex(s => s.value === search.value),
      );

      updateSessionSearches(JSON.stringify(uniqueSearches.slice(0, MAX_STORED_SEARCHES)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, fetchedSearches]);

  const recentSearchesParsed = useMemo(() => {
    const searches = isAuthenticated ? sessionSearches : recentSearches;
    const rawSearches =
      (searches && guardedJsonParse(parsedObjectIsRecentSearchArray, searches)) || [];
    return rawSearches.map(search => {
      const { label, sublabel } = formatRecentSearchLabel(search.label, search.sublabel);
      return {
        ...search,
        label,
        sublabel,
        groupName: 'recentSearches',
      };
    });
  }, [isAuthenticated, recentSearches, sessionSearches]);

  const filteredRecentSearches = useMemo(() => {
    return filterRecentSearchByRentalType(searchRentalType, recentSearchesParsed);
  }, [searchRentalType, recentSearchesParsed]);

  const addRecentSearch = useCallback(
    async (recentSearch: TRecentSearchOption) => {
      const updatedRecentSearches = recentSearchesParsed.filter(
        search => search.value !== recentSearch.value,
      );

      // convert snake case filters to camel case according to the ESearchFilters enum
      // to avoid issues in the UI (search requests still function with snake case as expected)
      const modifiedRecentSearch = recentSearch;
      let modifiedSearchQuery = modifiedRecentSearch.url;
      const splitQuery = modifiedSearchQuery.split('?');
      const [queryUrlPart, queryParamsPart] = splitQuery;
      if (queryParamsPart) {
        const params = new URLSearchParams(queryParamsPart);

        let hasSnakeCaseFilter = false;
        SNAKE_CASE_FILTERS.forEach(filter => {
          if (params.has(filter)) {
            const value = params.get(filter);
            if (value !== null) {
              hasSnakeCaseFilter = true;
              params.set(toCamelCase(filter), value);
              params.delete(filter);
            }
          }
        });
        if (hasSnakeCaseFilter) {
          modifiedSearchQuery = `${queryUrlPart}?${params.toString()}`;
          modifiedRecentSearch.url = modifiedSearchQuery;
        }
      }

      if (isAuthenticated) {
        try {
          const savedSearchResults = await saveRecentSearch(modifiedRecentSearch.url);
          await updateFetchedSearches(savedSearchResults, { revalidate: false });
        } catch {
          return;
        }
      }

      update(
        JSON.stringify(
          [modifiedRecentSearch, ...updatedRecentSearches].slice(0, MAX_STORED_SEARCHES),
        ),
      );
    },
    [isAuthenticated, recentSearchesParsed, update, updateFetchedSearches],
  );

  return useMemo(
    // Expose all recent searches and limit results when the user starts a search.
    // Auto complete may find older stored searches when filtering but by default we'll
    // show MAX_RECENT_SEARCHES.
    () => [filteredRecentSearches, { addRecentSearch }],
    [filteredRecentSearches, addRecentSearch],
  );
}
